/**
 * Dados pessoais visíveis nas capturas: e-mail, telefone, NIF, CPF, CNPJ, IBAN e cartão.
 * Só o tipo e a posição vão para o passo, nunca o texto encontrado. Os números com dígito
 * de controlo são validados, para não confundir com encomendas, preços ou datas.
 * (Vem do ManualMaker, mmpii.js.)
 */

export type PiiKind = 'email' | 'phone' | 'nif' | 'cpf' | 'cnpj' | 'iban' | 'card';

export interface PiiMatch {
  kind: PiiKind;
  start: number;
  end: number;
}

/** Área assinalada numa captura, em fração do ecrã gravado (0…1). */
export interface PiiArea {
  kind: PiiKind;
  x: number;
  y: number;
  w: number;
  h: number;
}

const LABELS: Record<PiiKind, [string, string]> = {
  email: ['e-mail', 'e-mails'],
  phone: ['telefone', 'telefones'],
  nif: ['NIF', 'NIF'],
  cpf: ['CPF', 'CPF'],
  cnpj: ['CNPJ', 'CNPJ'],
  iban: ['IBAN', 'IBAN'],
  card: ['cartão', 'cartões'],
};

const digits = (s: string) => s.replace(/\D/g, '');
const at = (s: string, i: number) => Number(s.charAt(i));

export function nifOk(d: string): boolean {
  if (!/^[1235689]\d{8}$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 8; i++) sum += at(d, i) * (9 - i);
  const c = 11 - (sum % 11);
  return (c >= 10 ? 0 : c) === at(d, 8);
}

export function cpfOk(d: string): boolean {
  if (!/^\d{11}$/.test(d) || /^(\d)\1{10}$/.test(d)) return false;
  for (let t = 9; t < 11; t++) {
    let s = 0;
    for (let i = 0; i < t; i++) s += at(d, i) * (t + 1 - i);
    if (((s * 10) % 11) % 10 !== at(d, t)) return false;
  }
  return true;
}

export function cnpjOk(d: string): boolean {
  if (!/^\d{14}$/.test(d) || /^(\d)\1{13}$/.test(d)) return false;
  const dv = (n: number) => {
    const w =
      n === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let s = 0;
    for (let i = 0; i < n; i++) s += at(d, i) * w[i]!;
    const r = s % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(12) === at(d, 12) && dv(13) === at(d, 13);
}

export function luhnOk(d: string): boolean {
  if (d.length < 13 || d.length > 19 || /^(\d)\1+$/.test(d)) return false;
  let s = 0;
  for (let i = 0; i < d.length; i++) {
    let x = at(d, d.length - 1 - i);
    if (i % 2) {
      x *= 2;
      if (x > 9) x -= 9;
    }
    s += x;
  }
  return s % 10 === 0;
}

export function ibanOk(s: string): boolean {
  const v = s.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(v)) return false;
  const r = v.slice(4) + v.slice(0, 4);
  let rem = 0;
  for (const ch of r) {
    const c = ch.charCodeAt(0);
    const n = c >= 65 ? String(c - 55) : ch;
    for (const dch of n) rem = (rem * 10 + Number(dch)) % 97;
  }
  return rem === 1;
}

type Rule = [RegExp, PiiKind | ((m: string) => PiiKind | '')];

// Por ordem de prioridade: os primeiros ganham as sobreposições.
const RULES: Rule[] = [
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g, 'email'],
  [
    /\b[A-Z]{2}\d{2}(?: ?[A-Z0-9]{4}){2,7}(?: ?[A-Z0-9]{1,4})?\b/g,
    (m) => (ibanOk(m) ? 'iban' : ''),
  ],
  [/\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/g, (m) => (cnpjOk(digits(m)) ? 'cnpj' : '')],
  [/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, (m) => (cpfOk(digits(m)) ? 'cpf' : '')],
  [
    /\b\d(?:[ -]?\d){12,18}\b/g,
    (m) => {
      const d = digits(m);
      if (d.length === 14 && cnpjOk(d)) return 'cnpj';
      return luhnOk(d) && /[ -]/.test(m) === /^(\d{4}[ -]){2,}/.test(m) ? 'card' : '';
    },
  ],
  [
    /\+\d{1,3}[\s.-]?(?:\(?\d{1,4}\)?[\s.-]?){2,5}\d{2,4}\b/g,
    (m) => {
      const n = digits(m).length;
      return n >= 9 && n <= 15 ? 'phone' : '';
    },
  ],
  [/\(\d{2}\)\s?9?\d{4}-?\d{4}\b/g, 'phone'],
  [/\b\d{11}\b/g, (m) => (cpfOk(m) ? 'cpf' : '')],
  [
    /\b\d{3}[ .]\d{3}[ .]\d{3}\b/g,
    (m) => {
      const d = digits(m);
      return /^(9[1236]|2)/.test(d) ? 'phone' : nifOk(d) ? 'nif' : '';
    },
  ],
  [/\b\d{9}\b/g, (m) => (nifOk(m) ? 'nif' : /^9[1236]/.test(m) ? 'phone' : '')],
];

const QUICK = /@|\d{3}/;

/** Texto → possíveis dados pessoais, por ordem de posição, sem sobreposições. */
export function findPii(text: string): PiiMatch[] {
  const s = String(text || '');
  if (s.length < 6 || !QUICK.test(s)) return [];
  const out: PiiMatch[] = [];
  for (const [re, kindOf] of RULES) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(s))) {
      const kind = typeof kindOf === 'function' ? kindOf(m[0]) : kindOf;
      const a = m.index;
      const b = m.index + m[0].length;
      if (!kind) continue;
      if (out.some((o) => a < o.end && b > o.start)) continue;
      out.push({ kind, start: a, end: b });
    }
  }
  return out.sort((x, y) => x.start - y.start);
}

/** O texto tem algum dado pessoal (e-mail, telefone, NIF…)? */
export function hasPii(text: string | null | undefined): boolean {
  return findPii(text ?? '').length > 0;
}

/** Texto com os dados pessoais trocados por «…» (para o que sai da conta, como a IA). */
export function redactPii(text: string, mask = '…'): string {
  const found = findPii(text);
  if (!found.length) return text;
  let out = '';
  let i = 0;
  for (const m of found) {
    out += text.slice(i, m.start) + mask;
    i = m.end;
  }
  return out + text.slice(i);
}

/** "2 e-mails, 1 telefone" */
export function piiSummary(list: { kind: PiiKind }[]): string {
  const count = new Map<PiiKind, number>();
  for (const p of list || []) if (LABELS[p.kind]) count.set(p.kind, (count.get(p.kind) || 0) + 1);
  return [...count].map(([k, n]) => n + ' ' + LABELS[k][n === 1 ? 0 : 1]).join(', ');
}

/** Lista vinda de uma página → só tipos conhecidos e rectângulos válidos (máx. 50). */
export function sanitizePii(list: unknown): PiiArea[] {
  if (!Array.isArray(list)) return [];
  const out: PiiArea[] = [];
  for (const p of list as Record<string, unknown>[]) {
    if (out.length >= 50 || !p || typeof p.kind !== 'string' || !(p.kind in LABELS)) continue;
    const r: Partial<PiiArea> = { kind: p.kind as PiiKind };
    let ok = true;
    for (const k of ['x', 'y', 'w', 'h'] as const) {
      const v = p[k];
      if (typeof v !== 'number' || !Number.isFinite(v) || v < -1 || v > 2) {
        ok = false;
        break;
      }
      r[k] = Math.round(v * 10000) / 10000;
    }
    if (ok && r.w! > 0 && r.h! > 0) out.push(r as PiiArea);
  }
  return out;
}
